import {lineOrientationFrom} from '@github-ui/diffs/diff-line-helpers'
import type {ClientDiffLine, DiffLine} from '../types'
import {matchLine, isEmptyDiffLine} from './line-helpers'
import type {LineRange} from '@github-ui/diffs/types'

type DiffRowSelectedCheckerReturn = {
  selectedLeftLines: ClientDiffLine[]
  selectedRightLines: ClientDiffLine[]
}

/**
 * From the line range, get the corresponding difflines from the provided left and right diffLines
 */
export default function DiffRowSelectedChecker({
  selectedLineRange,
  leftLines,
  rightLines,
}: {
  selectedLineRange: LineRange
  leftLines: ClientDiffLine[]
  rightLines?: ClientDiffLine[]
}): DiffRowSelectedCheckerReturn {
  const isSplit = !!rightLines
  const {startOrientation, endOrientation} = selectedLineRange

  const firstLineIndex = getLines(startOrientation).findIndex(line =>
    matchLine(line, selectedLineRange.startLineNumber, selectedLineRange.startOrientation),
  )
  const lastLineIndex = getLines(endOrientation).findIndex(line =>
    matchLine(line, selectedLineRange.endLineNumber, selectedLineRange.endOrientation),
  )

  const canHighlight = firstLineIndex >= 0 && lastLineIndex >= 0

  function isRowSelected(line: ClientDiffLine): boolean {
    if (!canHighlight || isEmptyDiffLine(line)) return false

    const lineIndex = getLines(lineOrientationFrom(line.type)).indexOf(line)

    // check if the line falls between the start and end highlighting index
    return lineIndex >= firstLineIndex && lineIndex <= lastLineIndex
  }

  function getLines(orientation: 'left' | 'right') {
    return !isSplit || isLeftLine(orientation) ? leftLines : rightLines
  }

  const selectedLeftLines = getLines('left').filter(line => isRowSelected(line))
  const selectedRightLines = getLines('right').filter(line => isRowSelected(line))

  return {
    selectedRightLines,
    selectedLeftLines,
  }
}

function isLeftLine(line: DiffLine): boolean
function isLeftLine(line: 'left' | 'right'): boolean
function isLeftLine(line: DiffLine | 'left' | 'right'): boolean {
  const orientation = typeof line === 'string' ? line : lineOrientationFrom(line.type)
  return orientation === 'left'
}
